@use '../../scss/utility';
@use './button-style-api';

:host .button {
  background: var(--btn-bg-color);
  color: var(--btn-text-color);
  border: var(--btn-border);

  &:focus {
    box-shadow: var(--btn-box-shadow);
  }

  &:hover:not([disabled]) {
    background: var(--btn-bg-color-hover);
  }

  &:active:not([disabled]) {
    background: var(--btn-bg-color-active);
  }
}

:host {
  display: inline-flex;
  position: relative;
  min-width: utility.wb-pixel-to-rem(144px);
  vertical-align: middle;

  .button {
    transition: var(--btn-transition);
    border-radius: var(--wb-radius-s);
    outline: none;
    box-sizing: border-box;
    flex: 1;

    cursor: pointer;
    appearance: none;
    text-decoration: none;
    overflow: visible;
    text-transform: none;
    outline-offset: 0;
    margin: 0;

    &::-moz-focus-inner {
      border-style: none;
      padding: 0;
    }

    // align icons and text
    display: inline-flex;
    gap: 0.5rem; // gap between slotted icon and text
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    text-align: center; // center text if button label breaks into two lines

    @include utility.wb-text(
      'l',
      (
        strong: true,
        responsive: false,
      )
    );
  }

  .wb-spinner {
    position: absolute;
    opacity: 0;
    /* stylelint-disable-next-line */
    top: 50%;
    /* stylelint-disable-next-line */
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    transition: opacity var(--btn-animation-duration) var(--wb-fade);
    --size: var(--spinner-size);
    cursor: pointer; // show pointer when the button is loading and not disabled
  }
}

::slotted(.wb-icon) {
  --size: #{utility.wb-pixel-to-rem(12px)};
  overflow: hidden;
  flex-shrink: 0;
}

::slotted(.wb-icon[name*='/']) {
  --size: #{utility.wb-pixel-to-rem(16px)};
}

:host([disabled]) {
  opacity: 0.4;
  cursor: not-allowed; // for the anchor

  .button {
    cursor: not-allowed; // for the button
  }

  slot,
  .wb-spinner {
    pointer-events: none; // prevent slotted elements and wb-spinner from emitting click events
  }
}

:host([size='l']) {
  --spinner-size: #{utility.wb-pixel-to-rem(32px)};

  .button {
    padding: utility.wb-pixel-to-rem(15px) utility.wb-pixel-to-rem(31px);
  }
}

:host([size='m']) {
  --spinner-size: #{utility.wb-pixel-to-rem(24px)};

  .button {
    padding: utility.wb-pixel-to-rem(11px) utility.wb-pixel-to-rem(23px);
  }
}

:host([size='s']) {
  --spinner-size: #{utility.wb-pixel-to-rem(16px)};

  .button {
    padding: utility.wb-pixel-to-rem(7px) utility.wb-pixel-to-rem(15px);
  }
}

:host([variant='tertiary'][size='l']) .button {
  padding-inline-start: utility.wb-pixel-to-rem(23px);
  padding-inline-end: utility.wb-pixel-to-rem(23px);
}

:host([variant='tertiary'][size='m']) .button {
  padding-inline-start: utility.wb-pixel-to-rem(15px);
  padding-inline-end: utility.wb-pixel-to-rem(15px);
}

:host([variant='tertiary'][size='s']) .button {
  padding-inline-start: utility.wb-pixel-to-rem(7px);
  padding-inline-end: utility.wb-pixel-to-rem(7px);
}

:host([loading]) {
  .button {
    color: transparent !important;
  }

  .wb-spinner {
    opacity: 1;
  }
}

:host([variant='tertiary']),
:host([icon-only]) {
  min-width: initial;
}

:host([icon-only]) .button {
  padding: 0;
  min-width: 0;
  font-size: 0;
  gap: 0;
}

:host([icon-only]) ::slotted(.wb-icon) {
  --size: #{utility.wb-pixel-to-rem(16px)};
}

:host([icon-only]) ::slotted(.wb-icon[name*='/']) {
  --size: #{utility.wb-pixel-to-rem(24px)};
}

:host([icon-only][size='l']) .button {
  width: utility.wb-pixel-to-rem(56px);
  height: utility.wb-pixel-to-rem(56px);
}

:host([icon-only][size='m']) .button {
  width: utility.wb-pixel-to-rem(48px);
  height: utility.wb-pixel-to-rem(48px);
}

:host([icon-only][size='s']) .button {
  width: utility.wb-pixel-to-rem(40px);
  height: utility.wb-pixel-to-rem(40px);
}
